import { TTopSummaries } from "domain/entities/top-summaries-response";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { createBlobDownloader } from "utils/files/downloadBlob";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { checkParamsConsoleOnly } from "helper/filtersHelper";

type TTopSummariesProviderProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource: IReportsRepository;
};

export type TTopSummariesState = {
  requestParamsIndex: number;
  topSummariesData?: TTopSummaries;
  downloadingReports?: boolean;
  downloadTopSummariesReport: () => void;
  isLoading: boolean;
};

const initContext: TTopSummariesState = {
  requestParamsIndex: 0,
  downloadTopSummariesReport: () => null,
  isLoading: true,
};
export const TopSummariesContext = createContext(initContext);

export const TopSummariesProvider = ({
  requestParamsIndex,
  children,
  reportsDatasource,
}: TTopSummariesProviderProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [topSummariesData, setTopSummariesData] = useState<any>();
  const [downloadingReports, setDownloadingReports] = useState(false);
  const { requestParams, comparisonEnabled } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  const downloadTopSummariesReport = useCallback(() => {
    setDownloadingReports(true);
    return reportsDatasource
      .exportTopSummariesCSV(currentRequestParams)
      .then(createBlobDownloader("console-summary.zip"))
      .finally(() => setDownloadingReports(false));
  }, [currentRequestParams]);

  const canShowPanel = useMemo(() => {
    if (comparisonEnabled) {
      return requestParams.every(checkParamsConsoleOnly);
    }

    return checkParamsConsoleOnly(currentRequestParams);
  }, [requestParams]);

  useEffect(() => {
    if (canShowPanel) {
      setIsLoading(true);
      reportsDatasource
        .getTopSummaries(currentRequestParams)
        .then(setTopSummariesData)
        .then(() => setIsLoading(false));
    }
  }, [currentRequestParams]);

  return (
    <TopSummariesContext.Provider
      value={{
        requestParamsIndex,
        downloadingReports,
        topSummariesData,
        downloadTopSummariesReport,
        isLoading,
      }}
    >
      {canShowPanel ? children : <></>}
    </TopSummariesContext.Provider>
  );
};
